import { http, fallback, unstable_connector } from 'wagmi';
import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import { enabledNetworks } from '@dapp/multi-lib/network';
import { injected } from 'wagmi/connectors';
import { Chain } from '@wagmi/core/chains';
import { defineChain } from 'viem';
import {
  injectedWallet,
  metaMaskWallet,
  baseAccount,
  trustWallet,
  rabbyWallet,
  walletConnectWallet,
} from '@rainbow-me/rainbowkit/wallets';

const projectId = process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID;

const chains = enabledNetworks.map((item) =>
  defineChain(item)
) as unknown as readonly [Chain, ...Chain[]];

type WagmiConfig = ReturnType<typeof getDefaultConfig>;

let _config: WagmiConfig | null = null;

function getConfig(): WagmiConfig {
  if (!_config) {
    _config = getDefaultConfig({
      appName: 'DApp',
      projectId: projectId!,
      chains: chains,
      batch: { multicall: true },
      multiInjectedProviderDiscovery: true,
      transports: chains.reduce((acc, chain) => {
        return {
          ...acc,
          [chain.id]: fallback([
            unstable_connector(injected),
            ...chain.rpcUrls.default.http.map((url) => http(url)),
          ]),
        };
      }, {}),
      ssr: false,
      wallets: [
        {
          groupName: 'Popular',
          wallets: [
            injectedWallet,
            metaMaskWallet,
            baseAccount,
            trustWallet,
            rabbyWallet,
            walletConnectWallet,
          ],
        },
      ],
    });
  }
  return _config;
}

// Proxy defers getDefaultConfig() until first property access in the browser.
// On server (SSR/build) all property accesses return undefined — no IndexedDB.
export const wagmiConfig: WagmiConfig = new Proxy({} as WagmiConfig, {
  get(_, prop, receiver) {
    if (typeof window === 'undefined') return undefined;
    return Reflect.get(getConfig(), prop, receiver);
  },
  set(_, prop, value) {
    if (typeof window === 'undefined') return true;
    return Reflect.set(getConfig(), prop, value);
  },
  has(_, prop) {
    if (typeof window === 'undefined') return false;
    return Reflect.has(getConfig(), prop);
  },
});

export const CONNECTOR_IDS = {
  SAFE_WALLET: 'safe',
  WALLET_CONNECT: 'walletConnect',
  METAMASK: 'metaMask',
} as const;

export type ConnectorID = keyof typeof CONNECTOR_IDS;
